/*
  Global AXIOS interceptor.
  can add logic here to intercept HTTP requests

*/
import axios from 'axios';
import store from 'redux/store/index';
import { logOutUser } from 'redux/auth/authService';
import { partnerSessionExpired } from 'redux/auth/authActions';
import endJobcaseSession from 'util/auth/endJobcaseSession';
import { hasEmployerToken, hasPartnerToken } from 'util/auth/token';
import lodash from 'lodash';
import { localStorageKey } from 'constants/index';
import {
   JOBCASE_EMAIL_SIGN_IN_URL,
   JOBCASE_REFRESH_URL,
   refreshJobcaseToken,
} from '../../../pages/auth/services/authPartners';

// Single-flight guard for the Jobcase gateway token refresh.
// The job-reporting page (and others) fire several gateway calls at once; when the access token has
// expired they all 401 together. refreshJobcaseToken() ROTATES the refresh token, so if each 401'd
// request refreshed on its own, only the first would succeed and every other would fail -> the whole
// session gets logged out (the "logged out over and over" loop). Instead, the first 401 performs the
// single refresh and every other concurrent 401 waits for it, then retries with the new token.
let isRefreshingJobcaseToken = false;
let jobcaseRefreshWaiters = [];
const releaseJobcaseRefreshWaiters = (newToken, refreshError) => {
   jobcaseRefreshWaiters.forEach((notify) => notify(newToken, refreshError));
   jobcaseRefreshWaiters = [];
};

const isRejectedRefreshCredential = (error) => [400, 401, 403].includes(error?.response?.status);

// A 401 whose rejected credential was the PARTNER token (authorization2). Two cases:
// requests to sjm (which validates only the partner JWT, whatever else was attached —
// dual-session users send both headers), and requests where the partner token was the
// only credential sent. In both, the DE/gateway sessions may still be perfectly valid.
const isPartnerCredentialFailure = (request) => (
   request?.url?.startsWith(process.env.REACT_APP_MS_SPONSORED_JOB_META_SECURE)
   || (!!request?.headers?.authorization2 && !request?.headers?.authorization3)
);

// AOE (ectools) rejecting a request says nothing about the partner or gateway
// sessions — on a partner-only session AOE always 401s (no DE credential exists),
// and it previously matched isPartnerCredentialFailure and KILLED the live partner
// token (or, dual-session, fell to the blanket logOutUser). AOE failures must only
// fail their own request.
const isAoeRequest = (request) => (
   !!request?.url?.startsWith(process.env.REACT_APP_AOE_SECURE_WITH_PATH)
);

// Add items in the request header
axios.interceptors.request.use((config) => {
   if (
      hasEmployerToken()
      && (config.url.indexOf('apilogin') === -1 && config.url.indexOf('logout') === -1)
      && !(config?.url?.startsWith(process.env.REACT_APP_FULL_DOMAIN_GATEWAY))
      && !(config?.url?.includes('ad-service.sensitive-marketplace-production.ojop.io'))
   ) {
      // Attach DE token in Authorization3 header
      config.headers.authorization3 = `Bearer ${ localStorage.getItem('token') }`;
   }

   if (
      hasPartnerToken()
      && (config.url.indexOf('apilogin') === -1 && config.url.indexOf('logout') === -1)
      && !(config?.url?.startsWith(process.env.REACT_APP_FULL_DOMAIN_GATEWAY))
      && !(config?.url?.includes('ad-service.sensitive-marketplace-production.ojop.io'))
   ) {
      // Attach Partner token in Authorization2 header
      config.headers.authorization2 = `Bearer ${ localStorage.getItem(localStorageKey.partnerToken) }`;
   }

   const rememberMe = localStorage.getItem(localStorageKey.rememberMe);

   if (!(config?.url?.startsWith(process.env.REACT_APP_FULL_DOMAIN_GATEWAY)) && rememberMe
   && !(config?.url?.includes('ad-service.sensitive-marketplace-production.ojop.io'))) {
      config.headers.rememberMe = rememberMe;
   }

   if (config?.url?.startsWith(process.env.REACT_APP_FULL_DOMAIN_GATEWAY)
      && localStorage.getItem(localStorageKey.jobcaseToken) !== null) {
      // Axios 0.21 preserves differently-cased header keys. Keep only the current token.
      delete config.headers.Authorization;
      config.headers.authorization = `Bearer ${ localStorage.getItem(localStorageKey.jobcaseToken) }`;
   }

   return config;
}, (error) => {
   // Do something with request error
   return Promise.reject(error);
});

// Add a response interceptor
axios.interceptors.response.use((response) => {
   // Any status code that lie within the range of 2xx cause this function to trigger
   // Do something with response data
   if (response.headers.refreshed_jwt3) {
      localStorage.setItem(localStorageKey.employerToken, response.headers.refreshed_jwt3);
   }

   if (response?.headers?.refreshed_jwt2) {
      localStorage.setItem(localStorageKey.partnerToken, response.headers.refreshed_jwt2);
   }

   return response;
}, async (error) => {
   // Any status codes that falls outside the range of 2xx cause this function to trigger
   // Do something with response error
   const status = error?.response?.status;
   const data = error?.response?.data;

   const originalRequest = error.config;
   // The refresh call must never try to refresh itself. If it fails, the original request's
   // refresh handler below decides whether to retry later or send the person to sign-in.
   if (originalRequest?.url === JOBCASE_REFRESH_URL) {
      return Promise.reject(error);
   }

   if (
      status === 401
      && originalRequest?.url?.startsWith(process.env.REACT_APP_FULL_DOMAIN_GATEWAY)
      && originalRequest?.url !== JOBCASE_EMAIL_SIGN_IN_URL
      // eslint-disable-next-line no-underscore-dangle
      && !originalRequest?._retry
   ) {
      // eslint-disable-next-line no-underscore-dangle
      originalRequest._retry = true;

      // A refresh is already in flight for another 401'd request — wait for it and reuse its result
      // instead of starting a second refresh (which would rotate the token out from under the first).
      if (isRefreshingJobcaseToken) {
         return new Promise((resolve, reject) => {
            jobcaseRefreshWaiters.push((newToken, refreshError) => {
               if (!newToken) {
                  reject(refreshError || error);
                  return;
               }
               originalRequest.headers.authorization = `Bearer ${ newToken }`;
               resolve(axios(originalRequest));
            });
         });
      }

      isRefreshingJobcaseToken = true;
      try {
         if (!localStorage.getItem(localStorageKey.jobcaseRefreshToken)) {
            isRefreshingJobcaseToken = false;
            releaseJobcaseRefreshWaiters(null, error);
            endJobcaseSession(store.dispatch);
            return Promise.reject(error);
         }
         const tokens = await refreshJobcaseToken();
         const accessToken = tokens?.access_token;
         const refreshToken = tokens?.refresh_token;
         isRefreshingJobcaseToken = false;

         if (!accessToken || !refreshToken) {
            releaseJobcaseRefreshWaiters(null, error);
            endJobcaseSession(store.dispatch);
            return Promise.reject(error);
         }

         releaseJobcaseRefreshWaiters(accessToken);
         originalRequest.headers.authorization = `Bearer ${ accessToken }`;
         return axios(originalRequest);
      } catch (refreshError) {
         isRefreshingJobcaseToken = false;
         releaseJobcaseRefreshWaiters(null, refreshError);
         if (isRejectedRefreshCredential(refreshError)) {
            endJobcaseSession(store.dispatch);
         }
         // A network error or 5xx refresh failure is temporary; keep the credentials so a
         // later list retry can recover without asking the person to sign in again.
         return Promise.reject(refreshError);
      }
   } else if (status === 401 && originalRequest?.url?.startsWith(process.env.REACT_APP_FULL_DOMAIN_GATEWAY)
      && originalRequest?.url !== JOBCASE_EMAIL_SIGN_IN_URL) {
      // A second 401 after a successful refresh means this gateway session is no longer usable.
      endJobcaseSession(store.dispatch);
   } else if (status === 401 && !isAoeRequest(originalRequest) && isPartnerCredentialFailure(originalRequest)) {
      // Partner-credential failure (sjm KEEP pages, partner section). Historically this
      // fell into the blanket logOutUser() below, which wiped ALL tokens — an expired
      // partner token logged users out of a perfectly valid DE session. Instead, drop
      // only the dead partner token; pages surface their "partner sign-in required"
      // state and every other session survives.
      store.dispatch(partnerSessionExpired());
   } else if (
      (
         originalRequest?.url !== JOBCASE_EMAIL_SIGN_IN_URL
         && status === 401
         && !isAoeRequest(originalRequest)
         && !localStorage.getItem('test')
      ) || (
         status === 500
         && data
         && lodash.isString(data)
         && data.indexOf('Expired or invalid JWT token') > -1
      )
   ) {
      store.dispatch(logOutUser());
   }

   return Promise.reject(error);
});
